import React, {useEffect, useRef} from 'react';
import Button, {ButtonStyle} from '@components/buttons/Button/Button';
import UnderlinedBtn from '@components/buttons/UnderlinedBtn';

export type BrowseFilesEvent = {
    file: File | undefined,
};

export type BrowseFilesClickEventHandler = (e: BrowseFilesEvent) => void;

interface BrowseFilesButtonProps {
    /**
     * File extensions to allow a User to select.
     * Example: '.pdf, .png'
     */
    allowedTypes?: string;

    /**
     * Setting this to true will reset the <input /> memory
     * for whatever file is currently selected.
     * When this is completed, 'onCacheCleared()' is called.
     */
    clearCache?: boolean;
    testId?: string;

    /**
     * Whether the file input is required for form validation
     */
    required?: boolean;

    /**
     * Button style variant
     * - 'default': Standard tertiary button
     * - 'subtle': Underlined text style for replace actions
     */
    variant?: 'default' | 'subtle';

    /**
     * Button label text (defaults to 'Browse files')
     */
    label?: string;

    /**
     * Called after the <input /> has been cleared. Will only
     * be called if 'clearCache' was set to true.
     * Subscribe to this to know that this action was completed.
     * When this is called, you can clear the variable that
     * set the 'clearCache' property.
     */
    onCacheCleared?: () => void;
    onClick?: BrowseFilesClickEventHandler;
}

export default function BrowseFilesButton(props: BrowseFilesButtonProps) {
    const browseFilesRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const onClickHandler = () => {
        browseFilesRef.current?.click();
    };

    const onChangeHandler = () => {
        /**
         * Because the <input /> may remove the file it's holding
         * in memory when you click cancel in the dialog, always
         * send the result, even if undefined to imply the file has
         * been removed. Does this matter? Affects form HTML
         * elements when associating inputs with form data.
         * From a User perspective, it would seem odd
         * to cancel and remove a file and not keep the existing
         * one.
         * So, left it up to the callee to decide what to do.
        */
        const newFile = browseFilesRef.current?.files?.[0];

        // Clear button validation when file is selected
        if (newFile) {
            const button = containerRef.current?.querySelector('button') as HTMLButtonElement;
            button?.setCustomValidity('');
        }

        props.onClick?.({ file: newFile });
    };

    useEffect(() => {
        // Only execute this effect if clear is set to true. This
        // guard may also prevents its execution after the first
        // render.
        if (!props.clearCache) return;

        if (browseFilesRef.current != null) {
            browseFilesRef.current.value = '';
        }

        props.onCacheCleared?.();
    }, [props.clearCache]);

    // Handle validation redirection for hidden inputs
    useEffect(() => {
        const input = browseFilesRef.current;
        if (!input) return;

        const handleInvalid = (e: Event) => {
            e.preventDefault();
            // Trigger validation popup on the visible button instead of the hidden input
            const button = containerRef.current?.querySelector('button') as HTMLButtonElement;
            if (button) {
                button.setCustomValidity('Please select a file');
                button.reportValidity();
            }
        };

        input.addEventListener('invalid', handleInvalid);
        return () => input.removeEventListener('invalid', handleInvalid);
    }, []);

    return (
        <div ref={containerRef} data-testid={props.testId}>
            <input ref={browseFilesRef} onChange={onChangeHandler} type='file' accept={props.allowedTypes}
                required={props.required} alt='Browse Files' style={{ display: 'none' }} />
            {props.variant === 'subtle' ? (
                <UnderlinedBtn text={props.label || 'Browse files'} onClickFn={onClickHandler} type='button' />
            ) : (
                    <Button label={props.label || 'Browse files'} onClickFunc={onClickHandler}
                        btnType={ButtonStyle.tertiary} type='button' />
            )}
        </div>
    );
}
